import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  StudentFilter as StudentFilterModel,
  StudentServiceFilter as StudentServiceFilterModel,
  StudentServiceListDto,
  StudentServiceListFilter,
  StudentServiceListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { StudentFilter } from "pages/students/students/StudentFilter";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { StudentServiceFilter } from "../StudentServiceFilter";
import { studentServicePageRoute } from "../StudentServicePage/StudentServicePage";
import { StudentServiceTable } from "./StudentServiceTable";

const routeName = "studentServices";

export const studentServiceListPageRoute: PageRoute = {
  handle: "Services",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StudentServiceListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentService;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentServiceListFilter>();
  const [initListFilter, setInitListFilter] = useState<StudentServiceListFilter>(listFilter);
  const [listItems, setListItems] = useState<StudentServiceListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentServiceListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentServices.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentServices.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    ApiService.studentServices.initListFilter().then((response) => {
      setListFilter(response);
      setInitListFilter(response);
      loadList(response);
    });

    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentServices.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentServiceListFilter>({
    maxWidth: "xl",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <Stack spacing="lg">
        <Stack spacing="lg">
          <Typography variant="h6" fontWeight={(theme) => theme.typography.fontWeightRegular}>
            Student:
          </Typography>

          <StudentFilter
            filter={listFilter.studentFilter || ({} as StudentFilterModel)}
            setFilter={(filter) =>
              setListFilter(
                produce(listFilter, (draft) => {
                  draft.studentFilter = filter;
                })
              )
            }
          />
        </Stack>

        <Stack spacing="lg">
          <Typography variant="h6" fontWeight={(theme) => theme.typography.fontWeightRegular}>
            Service:
          </Typography>

          <StudentServiceFilter
            filter={listFilter.serviceFilter || ({} as StudentServiceFilterModel)}
            setFilter={(filter) =>
              setListFilter(
                produce(listFilter, (draft) => {
                  draft.serviceFilter = filter;
                })
              )
            }
          />
        </Stack>
      </Stack>
    ),
  });

  const getIdsMethods = () => ApiService.studentServices.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{studentServiceListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate(studentServicePageRoute.getPath({ id: 0, crudMode: "edit", screenType: "standard" }))
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StudentServiceTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentServices={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
